import { Icon, Text } from '@puppet/react-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, isValid } from 'date-fns';
import { formatDistanceToNow, monthDayYear } from '@utils/date';
import { TokenExpiryStatusV1 } from '@puppet/cd4pe-client-ts';

const SOON_THRESHOLD_DAYS = 30;

type Props = {
  status: TokenExpiryStatusV1 | undefined;
  expiresAt: string | null | undefined;
};

const TokenExpiryInfo = ({ status, expiresAt }: Props) => {
  const { t, i18n } = useTranslation('codeDelivery');

  if (!status) {
    return null;
  }

  if (status === 'NO_EXPIRY') {
    return (
      <div className="source-control-card-token">
        <Text color="subtle">
          {t('sourceControl.integrations.tokenExpiry.noExpiration')}
        </Text>
      </div>
    );
  }

  if (status === 'UNAVAILABLE') {
    return (
      <div className="source-control-card-token">
        <Text color="subtle">
          {t('sourceControl.integrations.tokenExpiry.unavailable')}
        </Text>
      </div>
    );
  }

  // HAS_EXPIRY
  const date = expiresAt ? new Date(expiresAt) : null;
  if (!date || !isValid(date)) {
    return (
      <div className="source-control-card-token">
        <Text color="subtle">
          {t('sourceControl.integrations.tokenExpiry.unavailable')}
        </Text>
      </div>
    );
  }

  const daysUntil = differenceInCalendarDays(date, new Date());
  const relative = formatDistanceToNow(date, i18n.language, {
    addSuffix: true,
  });

  if (daysUntil < 0) {
    return (
      <div
        className="source-control-card-token"
        data-testid="token-expiry-expired"
      >
        <Icon
          type="error"
          size="small"
          className="source-control-card-token__icon"
        />
        <Text color="danger">
          {t('sourceControl.integrations.tokenExpiry.expired', { relative })}
        </Text>
      </div>
    );
  }

  if (daysUntil <= SOON_THRESHOLD_DAYS) {
    return (
      <div
        className="source-control-card-token"
        data-testid="token-expiry-soon"
      >
        <Icon
          type="alert"
          size="small"
          className="source-control-card-token__icon"
        />
        <Text color="warning">
          {t('sourceControl.integrations.tokenExpiry.expiresSoon', {
            relative,
          })}
        </Text>
      </div>
    );
  }

  return (
    <div
      className="source-control-card-token"
      data-testid="token-expiry-healthy"
    >
      <Text color="subtle">
        {t('sourceControl.integrations.tokenExpiry.expiresOn', {
          date: monthDayYear(date.toISOString()) ?? '',
        })}
      </Text>
    </div>
  );
};

export default TokenExpiryInfo;
